import {
  Avatar,
  Button,
  Media,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useSnackbarContext,
} from "@ameelio/ui";
import { useMutation, useQuery } from "@apollo/client/react";
import { Box, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { OrganizationMembershipRole } from "../api/graphql";
import useApolloErrorHandler from "../lib/handleApolloError";
import Link from "../lib/Link";
import { useCurrentMembership } from "../SessionBoundary";
import { CreateOrganizationInvitationDocument } from "./CreateOrganizationInvitation.generated";
import EditOrganizationMembershipModal from "./EditOrganizationMembershipModal";
import { GetOrganizationMembershipsDocument } from "./GetOrganizationMemberships.generated";
import RemoveOrganizationMembershipModal from "./RemoveOrganizationMembershipModal";
import ResendInvitationLinkButton from "./ResendInvitationLinkButton";
import RevokeOrganizationInvitationButton from "./RevokeOrganizationInvitationButton";

type Props = {
  organizationId: string;
};

export default function OrganizationMemberships({ organizationId }: Props) {
  const { t } = useTranslation();
  const { id: currentMembershipId, role } = useCurrentMembership();
  const [membershipIdToRemove, setMembershipIdToRemove] = useState<string>();
  const [membershipIdToEdit, setMembershipIdToEdit] = useState<string>();
  const isAdmin = role === OrganizationMembershipRole.Admin;
  const handleApolloError = useApolloErrorHandler();
  const snackbarContext = useSnackbarContext();

  const [resendInvitation] = useMutation(CreateOrganizationInvitationDocument, {
    onError: (e) => handleApolloError(e),
    onCompleted: () =>
      snackbarContext.alert("info", t("Invitation email resent successfully")),
  });

  const { data, error } = useQuery(GetOrganizationMembershipsDocument, {
    fetchPolicy: "cache-and-network",
    variables: { organizationId },
  });

  if (error) throw error;
  if (!data) return <Box />;

  const { organization } = data;
  const { memberships: teamMembers } = data.organization;
  const membershipToEdit = teamMembers.find(
    (tm) => tm.id === membershipIdToEdit
  );
  const invitations = [...data.organization.invitations].sort((a, b) =>
    a.email.localeCompare(b.email)
  );

  return (
    <TableContainer>
      <RemoveOrganizationMembershipModal
        organizationMembershipId={membershipIdToRemove}
        onSuccess={() => setMembershipIdToRemove(undefined)}
        onCancel={() => setMembershipIdToRemove(undefined)}
        onFail={(e) => {
          setMembershipIdToRemove(undefined);
          handleApolloError(e);
        }}
      />
      {membershipToEdit && (
        <EditOrganizationMembershipModal
          organizationMembership={membershipToEdit}
          onClose={() => setMembershipIdToEdit(undefined)}
          onRemove={
            membershipToEdit.role === OrganizationMembershipRole.Admin
              ? undefined
              : () => {
                  setMembershipIdToEdit(undefined);
                  setMembershipIdToRemove(membershipToEdit.id);
                }
          }
        />
      )}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{t("Name")}</TableCell>
            <TableCell>{t("Email")}</TableCell>
            <TableCell>{t("Role")}</TableCell>
            <TableCell>{t("Actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {teamMembers.map((tm) => (
            <TableRow key={tm.id}>
              <TableCell>
                <Link
                  sx={{ textDecoration: "none" }}
                  to={`/organization/${organization.id}/team-member/${tm.id}`}
                >
                  <Media image={<Avatar contact={tm.visitor} />}>
                    <Typography
                      variant="body2"
                      sx={{ textDecoration: "underline" }}
                    >
                      {tm.visitor.fullName}
                    </Typography>
                  </Media>
                </Link>
              </TableCell>
              <TableCell>
                <Typography variant="body2">{tm.visitor.email}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {
                    {
                      [OrganizationMembershipRole.Admin]: t("Admin"),
                      [OrganizationMembershipRole.Member]: t("Member"),
                    }[tm.role]
                  }
                </Typography>
              </TableCell>
              <TableCell>
                {(isAdmin ? tm.id !== currentMembershipId : true) && (
                  <Button
                    disabled={!isAdmin}
                    variant="outlined"
                    disabledReason={t(
                      "Only organization admins may change roles"
                    )}
                    onClick={() => setMembershipIdToEdit(tm.id)}
                  >
                    {t("Edit")}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
          {invitations.map((i) => (
            <TableRow key={i.id}>
              <TableCell>
                <Media image={<Avatar />}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.disabled",
                    }}
                  >
                    -
                  </Typography>
                </Media>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.disabled",
                  }}
                >
                  {i.email}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.disabled",
                  }}
                >
                  {
                    {
                      [OrganizationMembershipRole.Admin]: t("Admin"),
                      [OrganizationMembershipRole.Member]: t("Member"),
                    }[i.role]
                  }{" "}
                  ({t("Invite pending")})
                </Typography>
              </TableCell>
              <TableCell>
                <Stack
                  direction="row"
                  sx={{
                    gap: 1,
                  }}
                >
                  <ResendInvitationLinkButton
                    disabled={!isAdmin}
                    disabledReason={t(
                      "Only organization admins may invite members"
                    )}
                    onClick={() => {
                      resendInvitation({
                        variables: {
                          input: {
                            organizationId,
                            email: i.email,
                            role: i.role,
                          },
                        },
                      });
                    }}
                  />
                  <RevokeOrganizationInvitationButton
                    isAdmin={isAdmin}
                    organizationInvitationId={i.id}
                  />
                </Stack>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
